import { makeAutoObservable, runInAction } from 'mobx'
import { message, notification } from 'antd'
import { openApi } from '../../../helpers/request'
import { storeLocal } from '../../../helpers/stores'
import i from '../../../translations/i'
import { LIVE_STORAGE_KEY } from './carts.controller'
import { FLASH_MS, LIVE_INTERVAL_MS, errorMessage, fromDisplay } from './carts.helpers'

class CartController {

    id: number | null = null
    cart: any = null
    notFound: boolean = false

    /** Склад для цен. null — сервер возьмёт склад последнего заказа пользователя */
    warehouseId: number | null = null

    /** Идёт правка — live-обновление не должно перетереть её результат старыми данными */
    busy: boolean = false

    /** Количество, которое менеджер набирает в поле, но ещё не сохранил. Ключ — id строки */
    drafts: Record<number, number | null> = {}

    flashIds: number[] = []
    updatedAt: Date | null = null
    live: boolean = storeLocal.get(LIVE_STORAGE_KEY) === true

    private timer: ReturnType<typeof setInterval> | null = null
    private requestId: number = 0
    private knownItems: Record<number, string | null> = {}

    constructor() {
        makeAutoObservable<CartController, 'timer' | 'requestId' | 'knownItems'>(this, {
            timer: false,
            requestId: false,
            knownItems: false,
        })
    }

    private path(suffix: string = ''): string {
        return `/carts/${this.id}${suffix}${this.warehouseId ? `?warehouse_id=${this.warehouseId}` : ''}`
    }

    open(id: number) {
        this.id = id
        this.cart = null
        this.notFound = false
        this.warehouseId = null
        this.drafts = {}
        this.flashIds = []
        this.knownItems = {}
        this.load()
    }

    load(silent: boolean = false) {
        if (!this.id) return

        const requestId = ++this.requestId

        openApi('get', this.path(), {}, undefined, silent)
            .then(result => runInAction(() => {
                if (requestId !== this.requestId) return
                this.apply(result.data, silent)
            }))
            .catch(err => runInAction(() => {
                if (err?.response?.status === 404 && !silent) {
                    this.notFound = true
                    return
                }

                if (silent) console.log(err)
                else notification.error({ message: i.t('carts:errors.common'), description: errorMessage(err) })
            }))
    }

    private apply(cart: any, silent: boolean) {
        const items = [...(cart.items ?? []), ...(cart.gifts ?? [])]

        // Подсвечиваем то, что поменял сам пользователь, пока менеджер смотрит корзину
        if (silent && this.cart) {
            const changed = items
                .filter((item: any) => this.knownItems[item.id] !== item.updatedAt)
                .map((item: any) => item.id)

            if (changed.length > 0) {
                this.flashIds = changed
                setTimeout(() => runInAction(() => {
                    this.flashIds = this.flashIds.filter((id: number) => !changed.includes(id))
                }), FLASH_MS)
            }
        }

        this.knownItems = {}
        items.forEach((item: any) => { this.knownItems[item.id] = item.updatedAt })

        this.cart = cart
        this.notFound = false
        this.updatedAt = new Date()

        // Сервер мог сам выбрать склад — держим выбор в синхроне, чтобы правки считались по нему же
        if (cart.warehouse && !this.warehouseId) this.warehouseId = cart.warehouse.id
    }

    setWarehouse(id: number | null) {
        this.warehouseId = id
        this.load()
    }

    /** Правка корзины: сервер отвечает свежей карточкой, второй запрос не нужен */
    private mutate(method: 'post' | 'put' | 'delete', suffix: string, data: any, success: string): Promise<boolean> {
        this.busy = true
        // Ответ на правку свежее любого фонового обновления, которое летит прямо сейчас
        this.requestId++

        return openApi(method, this.path(suffix), data)
            .then(result => {
                runInAction(() => this.apply(result.data, false))
                message.success(success)

                return true
            })
            .catch(err => {
                notification.error({ message: i.t('carts:errors.common'), description: errorMessage(err) })

                // Чаще всего пользователь успел поменять корзину сам — показываем, что в ней сейчас
                if (err?.response?.status === 404) this.load(true)

                return false
            })
            .finally(() => runInAction(() => { this.busy = false }))
    }

    setDraft(itemId: number, value: number | null) {
        this.drafts[itemId] = value
    }

    /** value — количество в единицах товара, как его видит менеджер */
    saveQuantity(item: any, value: number | null) {
        delete this.drafts[item.id]

        if (value === null || value <= 0) return

        const count = fromDisplay(value, item.step)
        if (Math.abs(count - item.count) < 0.0005) return

        this.mutate('put', `/items/${item.id}`, { count }, i.t('carts:detail.saved'))
    }

    removeItem(item: any) {
        this.mutate('delete', `/items/${item.id}`, {}, i.t('carts:detail.removed'))
    }

    clear() {
        this.mutate('delete', '/items', {}, i.t('carts:detail.cleared'))
    }

    removePromoCode() {
        this.mutate('delete', '/promo_code', {}, i.t('carts:detail.promoRemoved'))
    }

    /** quantity — уже в шагах товара */
    addItem(product: any, quantity: number, modifiers: { group_id: number, modifier_id: number, count: number }[]): Promise<boolean> {
        return this.mutate('post', '/items', {
            product_id: product.id,
            quantity,
            modifiers,
        }, i.t('carts:detail.added', { name: product.name }))
    }

    setLive(value: boolean) {
        this.live = value
        storeLocal.set(LIVE_STORAGE_KEY, value)

        if (value) {
            this.load(true)
            this.startLive()
        } else {
            this.stopLive()
        }
    }

    startLive() {
        this.stopLive()
        if (!this.live) return

        this.timer = setInterval(() => {
            // Не мешаем менеджеру: пока идёт правка или набирается количество, ждём
            const typing = Object.values(this.drafts).some((value) => value !== undefined)
            if (!document.hidden && !this.busy && !typing) this.load(true)
        }, LIVE_INTERVAL_MS)
    }

    stopLive() {
        if (this.timer) clearInterval(this.timer)
        this.timer = null
    }
}

export default new CartController()
